import React, {
  FC, useEffect, useRef, useState,
} from 'react';
import ReactModal from 'react-modal';
import getClassNames from '../../utils/getClassNames';
import Header from './Header';
import Body from './Body';
import Footer from './Footer';
import Title from './Title';
import Description from './Description';
import CloseButton from '../CloseButton';
import styles from './Modal.module.scss';
import { ModalClassNameModifier } from './ModalClassNameModifiers';

export type ModalProps = {
  children: React.ReactNode;
  modalOpen: boolean;
  closeModal: () => void;
  onAfterOpen?: () => void;
  label: string;
  noExit?: boolean;
  classNameModifier?: ModalClassNameModifier;
  className?: string;
  closeTimeoutMS?: number;
  /**
   * Boolean indicating if the modal should use the preventScroll flag when
   * restoring focus to the element that had focus prior to its display.
   */
  preventScroll?: boolean;
};

export type CompoundModal = FC<ModalProps> & {
  Header: typeof Header;
  Body: typeof Body;
  Footer: typeof Footer;
  Title: typeof Title;
  Description: typeof Description;
};

const Modal: CompoundModal = ({
  modalOpen,
  closeModal,
  label,
  children,
  noExit,
  classNameModifier,
  className = '',
  closeTimeoutMS,
  onAfterOpen,
  preventScroll,
}) => {
  const ready = useRef(false);
  const [isClosing, setClosing] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      ReactModal.setAppElement('#__next');
      ready.current = true;
    }
  }, []);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    // Always get enabled whenever 'open' is changed as long as animation is intended
    // (won't start visible)
    setClosing(!!closeTimeoutMS);

    if (!modalOpen) {
      timeout = setTimeout(() => setClosing(false), closeTimeoutMS);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [closeTimeoutMS, modalOpen]);

  return (
    <ReactModal
      // fix react 18 issue where react modal body class isn't removed on unmount
      // https://github.com/reactjs/react-modal/issues/808
      isOpen={modalOpen && ready.current}
      contentLabel={label}
      onAfterOpen={onAfterOpen}
      onRequestClose={closeModal}
      className={`${getClassNames(styles, 'content', classNameModifier)} ${className}`}
      overlayClassName={getClassNames(styles, 'overlay', classNameModifier)}
      shouldCloseOnOverlayClick={!noExit}
      shouldCloseOnEsc={!noExit}
      closeTimeoutMS={closeTimeoutMS}
      preventScroll={preventScroll}
    >
      {(modalOpen || isClosing) && (
        <>
          {children}
          {!noExit && (
            <CloseButton
              onClick={closeModal}
              size="large"
            />
          )}
        </>
      )}
    </ReactModal>
  );
};

Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;
Modal.Title = Title;
Modal.Description = Description;

export default Modal;
